import { useState, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '@/context/AuthContext';
import FiltersContext, { OptionPathsByFilter } from '@/context/FiltersContext/FiltersContext';
import { rehydrateFilterState, getDefaultFiltersState } from '@/context/FiltersContext/FiltersContextProvider';
import { useToast } from '@/storybook/components/Toast/Toast';
import { UserFilterListItem } from '@/models/user/userFilter';
import type { FilterState } from '@/types/filter/filterState';
import { recalculatePresetDateRanges } from '@/hooks/useApplySharedView.utils';
import { AppliedFilterSetStorage } from '@/utils/filter/appliedFilterSetStorage';
import { applyLocalFiltersWithRetry } from '@/utils/filter/fetchAndApplyFilter';
import fetchFilterPayload from '@/utils/filter/fetchFilterPayload';
import { writeCachedCountryCodes } from '@/models/filter/configs/general/countryFilter';
import useSharedViewValidation, { SharedViewScenario } from '@/hooks/useSharedViewValidation';
import { SharedViewDialogProps } from '@/components/filter/SharedViewDialog/SharedViewDialog';

interface UseApplyUserFilterOptions {
  filters: UserFilterListItem[];
  onBeforeApply?: () => void;
  onSuccess?: () => void;
}

interface ValidationDialogState {
  validFilters: FilterState;
  missingFilters: { filterKey: string; filterLabel: string; missingValues: string[] }[];
  localFiltersData: Record<string, Record<string, unknown>>;
  filterMetadata: UserFilterListItem;
  optionPaths?: OptionPathsByFilter;
}

interface UseApplyUserFilterReturn {
  applyFilter: (filterId: string) => Promise<void>;
  isApplying: boolean;
  validationDialogProps: SharedViewDialogProps | null;
}

const useApplyUserFilter = ({
  filters,
  onBeforeApply,
  onSuccess,
}: UseApplyUserFilterOptions): UseApplyUserFilterReturn => {
  const { t } = useTranslation();
  const { makeToast } = useToast();
  const { handleApplyFilter, filterConfig, replaceOptionPaths } = useContext(FiltersContext);
  const { userInfo } = useAuthContext();
  const { validateSharedFilters } = useSharedViewValidation();
  const [isApplying, setIsApplying] = useState(false);
  const [dialogState, setDialogState] = useState<ValidationDialogState | null>(null);

  const applyValidatedFilters = useCallback(
    (
      validFilters: FilterState,
      localFiltersPayload: Record<string, Record<string, unknown>>,
      filterMetadata: UserFilterListItem,
      optionPaths: OptionPathsByFilter | undefined,
      wasModified = false,
    ) => {
      onBeforeApply?.();

      const config = filterConfig ?? [];
      const defaultState = getDefaultFiltersState(config);
      const rehydrated = rehydrateFilterState(validFilters, defaultState);

      // The registry moves to the incoming selection only on a confirmed apply,
      // never during fetch/validation — otherwise a cancelled missing-values
      // dialog would leave the paths ahead of the still-applied filter state.
      // A whole-filter replacement without paths clears the registry so a stale
      // hierarchy from a previous selection is not read into the new query.
      if (replaceOptionPaths) {
        replaceOptionPaths(optionPaths ?? {});
      }

      applyLocalFiltersWithRetry(localFiltersPayload);
      handleApplyFilter(rehydrated);

      AppliedFilterSetStorage.save(filterMetadata, rehydrated, localFiltersPayload);

      onSuccess?.();
      const successKey = wasModified
        ? 'global.filters.manageSavedFilters.applyModifiedSuccess'
        : 'global.filters.manageSavedFilters.applySuccess';
      makeToast({ content: String(t(successKey)), appearance: 'success' });
    },
    [filterConfig, handleApplyFilter, replaceOptionPaths, onBeforeApply, onSuccess, makeToast, t],
  );

  const applyFilter = useCallback(
    async (filterId: string) => {
      setIsApplying(true);
      try {
        const filterMetadata = filters.find(savedFilter => savedFilter.userFilterId === filterId);
        if (!filterMetadata) throw new Error('Filter not found');

        const {
          globalFilters: rawGlobalFilters,
          localFilters,
          resolvedCountryCodes,
          senderCountryCodes,
          optionPaths,
        } = await fetchFilterPayload(filterId);
        const globalFilters = recalculatePresetDateRanges(rawGlobalFilters);
        const localFiltersPayload = localFilters ?? {};

        // The country filter resolves its Cube country_code values from a
        // module/session cache. Seed it from the saved payload so the second
        // (and subsequent) applies don't reuse stale codes from a prior set,
        // which would leave queries unchanged and skip the refetch.
        if (resolvedCountryCodes?.length) {
          writeCachedCountryCodes(resolvedCountryCodes);
        }

        const userCountryCodes = senderCountryCodes ?? userInfo?.countries ?? [];
        const validation = await validateSharedFilters(
          globalFilters,
          userCountryCodes,
          localFilters,
          resolvedCountryCodes,
          true,
        );

        if (validation.scenario === SharedViewScenario.VALUES_NOT_FOUND) {
          setDialogState({
            validFilters: validation.validFilters,
            missingFilters: validation.missingFilters,
            localFiltersData: validation.validLocalFilters ?? localFiltersPayload,
            filterMetadata,
            optionPaths,
          });
        } else {
          applyValidatedFilters(
            validation.validFilters,
            validation.validLocalFilters ?? localFiltersPayload,
            filterMetadata,
            optionPaths,
          );
        }
      } catch {
        makeToast({ content: String(t('global.filters.manageSavedFilters.applyError')), appearance: 'error' });
      } finally {
        setIsApplying(false);
      }
    },
    [filters, userInfo, validateSharedFilters, applyValidatedFilters, makeToast, t],
  );

  const handleDialogApply = useCallback(() => {
    if (!dialogState) return;
    applyValidatedFilters(
      dialogState.validFilters,
      dialogState.localFiltersData,
      dialogState.filterMetadata,
      dialogState.optionPaths,
      true,
    );
    setDialogState(null);
  }, [dialogState, applyValidatedFilters]);

  const handleDialogCancel = useCallback(() => {
    setDialogState(null);
  }, []);

  const validationDialogProps: SharedViewDialogProps | null = dialogState
    ? {
        isOpen: true,
        scenario: SharedViewScenario.VALUES_NOT_FOUND,
        missingFilters: dialogState.missingFilters,
        source: 'savedFilter',
        onApply: handleDialogApply,
        onCancel: handleDialogCancel,
      }
    : null;

  return { applyFilter, isApplying, validationDialogProps };
};

export default useApplyUserFilter;
